/**
 * User Rentals hook using React Relay + React Query
 *
 * Fetches rentals for a user separately from the main user query,
 * filtered by systemId. Intended to be called only when the Rentals tab is active.
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries
 * to regenerate the type artifacts.
 */

import { useQuery } from '@tanstack/react-query';
import { fetchQuery } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type {
  useUserRentalsQueryQuery as UseUserRentalsQueryQuery,
  RentalFilterInput,
} from './__generated__/useUserRentalsQueryQuery.graphql';
import type { UserRentalGraphQL } from './useUserDetailRelay';

const UserRentalsQuery = graphql`
  query useUserRentalsQueryQuery(
    $filter: UserFilterInput!
    $rentalFilter: RentalFilterInput
  ) {
    listUsers(first: 1, filter: $filter) {
      edges {
        node {
          id
          rentals(
            limit: 50
            sort: [{ field: CREATED_ON, order: DESC }]
            filter: $rentalFilter
          ) {
            id
            oldId
            createdOn
            completed
            duration
            chargeAmount
            startTime
            endTime
            endBy
            hardware {
              id
              oldId
              number
              hardwareType {
                id
                name
              }
            }
            startLocation {
              id
              oldId
              name
            }
            endLocation {
              id
              oldId
              name
            }
            system {
              id
              oldId
              name
            }
          }
        }
      }
    }
  }
`;

/**
 * Builds the rental filter for a user's rentals list: the system/organization
 * scope AND'd with a search or-group over start/end location names and the
 * hardware number. The or-group must stay at the rental level — the API
 * rejects `or` nested inside relationship filters.
 */
export function buildUserRentalsFilter(
  scope: { systemId?: string; organizationId?: string },
  search?: string
): RentalFilterInput | undefined {
  const filter: RentalFilterInput = {};

  if (scope.organizationId) {
    filter.organizationId = { eq: scope.organizationId };
  } else if (scope.systemId) {
    filter.systemId = { eq: scope.systemId };
  }

  if (search) {
    const term = `%${search}%`;
    filter.or = [
      { startLocation: { name: { ilike: term } } },
      { endLocation: { name: { ilike: term } } },
      { hardware: { numberText: { ilike: term } } },
    ];
  }

  return Object.keys(filter).length > 0 ? filter : undefined;
}

async function fetchUserRentals(
  phone: string,
  systemId?: string,
  organizationId?: string,
  search?: string
): Promise<UserRentalGraphQL[]> {
  const rentalFilter = buildUserRentalsFilter({ systemId, organizationId }, search);

  const result = await fetchQuery<UseUserRentalsQueryQuery>(
    relayEnvironment,
    UserRentalsQuery,
    {
      filter: {
        phone: { eq: phone },
      },
      rentalFilter,
    }
  ).toPromise();

  return (result?.listUsers?.edges?.[0]?.node?.rentals as UserRentalGraphQL[]) ?? [];
}

export function useUserRentalsQuery(
  phone: string | undefined,
  options?: {
    enabled?: boolean;
    systemId?: string;
    organizationId?: string;
    search?: string;
  }
) {
  return useQuery({
    queryKey: [
      'userRentals',
      phone,
      options?.systemId,
      options?.organizationId,
      options?.search,
    ],
    queryFn: () =>
      fetchUserRentals(
        phone!,
        options?.systemId,
        options?.organizationId,
        options?.search
      ),
    enabled: options?.enabled !== false && Boolean(phone),
    // Keep showing the previous page of results while a new search refetches
    placeholderData: (previousData: UserRentalGraphQL[] | undefined) => previousData,
  });
}
